import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import BrandLogo from "@/components/BrandLogo";
import { LogIn, ShieldCheck, Eye, EyeOff, Mail, Lock } from "lucide-react";

function Brand() {
  return <BrandLogo size="md" />;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();

  // Surface session-invalidated / expired notice exactly once after redirect.
  // Rendered as an inline banner (more reliable than toast across route remounts).
  const [securityNotice, setSecurityNotice] = useState("");
  useEffect(() => {
    const reason = sessionStorage.getItem("dw_logout_reason");
    if (!reason) return;
    sessionStorage.removeItem("dw_logout_reason");
    if (reason === "session_invalidated") {
      setSecurityNotice("Your account has been signed in on another device. For security reasons, your previous session has been ended.");
    } else if (reason === "expired") {
      setSecurityNotice("Your session has expired. Please sign in again.");
    }
  }, []);

  const links = [
    { to: "/", label: t("home") },
    { to: "/about", label: t("about_us") },
    { to: "/faq", label: t("faq") },
    { to: "/contact", label: t("contact") },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`${t("welcome")}, ${u.first_name}!`);
      nav((u.role === "admin" || u.role === "super_admin") ? "/admin" : "/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="login-portal relative min-h-screen overflow-hidden isolate" style={{ backgroundColor: "#f4f3ef" }}>
      {/* Calm, elegant background — light beige tone with very subtle navy ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{
          background:
            "radial-gradient(ellipse 80% 60% at 18% 12%, rgba(11,31,58,0.06), transparent 60%)," +
            "radial-gradient(ellipse 70% 50% at 88% 88%, rgba(212,175,55,0.05), transparent 60%)," +
            "linear-gradient(180deg, #faf9f6 0%, #f4f3ef 100%)"
        }} />
        <div className="absolute inset-0 portal-grid" />
      </div>

      {/* Top bar */}
      <header className="relative z-20 bg-white/60 backdrop-blur-md border-b border-zinc-200/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo"><Brand /></Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  loc.pathname === l.to
                    ? "bg-[#0b1f3a] text-white"
                    : "text-zinc-700 hover:text-[#0b1f3a]"
                }`}>
                {l.label}
              </Link>
            ))}
          </nav>
          <LanguageToggle />
        </div>
      </header>

      {/* Centered login card */}
      <main className="relative z-10 flex items-center justify-center px-4 py-12 sm:py-16 min-h-[calc(100vh-3.5rem)]">
        <div className="w-full max-w-[480px]">
          <div className="relative rounded-2xl bg-white/85 backdrop-blur-xl border border-white shadow-[0_30px_60px_-25px_rgba(11,31,58,0.18)] px-9 py-10 sm:px-11 sm:py-12 overflow-hidden animate-[fadeup_500ms_cubic-bezier(.2,.7,.2,1)_both]">
            {/* very subtle red corner accent */}
            <div className="absolute -top-20 -right-20 w-44 h-44 rounded-full bg-[var(--accent)]/8 blur-3xl pointer-events-none" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">
                <ShieldCheck size={12} className="text-[var(--accent)]" /> Secure Portal
              </div>
              <h1 className="font-display text-[28px] sm:text-[32px] font-bold tracking-tight mt-3 leading-tight text-[#0b1f3a]">
                Welcome to <span className="text-[var(--accent)]">Pelico Data</span>
              </h1>
              <p className="text-sm text-zinc-600 mt-2.5">Sign in to your secure work portal.</p>

              {securityNotice && (
                <div data-testid="security-notice" role="alert"
                  className="mt-5 flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] leading-relaxed text-rose-900">
                  <ShieldCheck size={16} className="shrink-0 mt-0.5 text-[var(--accent)]" />
                  <span>{securityNotice}</span>
                </div>
              )}

              <form onSubmit={submit} className="mt-9 space-y-6" data-testid="login-form">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 font-semibold">{t("email")}</label>
                  <div className="relative mt-2.5">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input data-testid="login-email" required type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="name@company.com"
                      className="w-full rounded-xl bg-white border border-zinc-200 pl-10 pr-3 py-3.5 text-sm text-[#0b1f3a] placeholder:text-zinc-400 focus:border-[#0b1f3a] focus:ring-4 focus:ring-[#0b1f3a]/8 outline-none transition-all" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 font-semibold">{t("password")}</label>
                  <div className="relative mt-2.5">
                    <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <input data-testid="login-password" required type={show ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" placeholder="••••••••"
                      className="w-full rounded-xl bg-white border border-zinc-200 pl-10 pr-11 py-3.5 text-sm text-[#0b1f3a] placeholder:text-zinc-400 focus:border-[#0b1f3a] focus:ring-4 focus:ring-[#0b1f3a]/8 outline-none transition-all" />
                    <button type="button" data-testid="toggle-password" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide" : "Show"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-[#0b1f3a] transition-colors">
                      {show ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <button data-testid="login-submit" disabled={busy}
                  className="login-cta group relative w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-semibold text-sm transition-all active:translate-y-[1px] disabled:opacity-60 disabled:cursor-not-allowed mt-2">
                  {busy ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      {t("signing_in")}
                    </>
                  ) : (
                    <>{t("sign_in")} <LogIn size={16} className="transition-transform group-hover:translate-x-0.5" /></>
                  )}
                </button>
              </form>

              <p className="mt-5 text-center text-xs text-zinc-500 leading-relaxed">
                Please login using the credentials provided by Pelico Data. Contact support if you face any login issues.
              </p>

              <div className="mt-8 pt-5 border-t border-zinc-100 flex items-center justify-center gap-2 text-xs text-zinc-500">
                <ShieldCheck size={13} className="text-[var(--accent)]" />
                <span className="font-medium tracking-wide">Secure Assessment Portal</span>
              </div>
            </div>
          </div>

          <p className="text-center text-[11px] text-zinc-500 mt-6">
            © {new Date().getFullYear()} Pelico Data · Authorized access only.
          </p>
        </div>
      </main>

      <style>{`
        @keyframes fadeup {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .login-portal .portal-grid {
          background-image:
            linear-gradient(rgba(11,31,58,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(11,31,58,0.04) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse at center, black 30%, transparent 78%);
          -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 78%);
        }
        .login-portal .login-cta {
          background: #0b1f3a;
          box-shadow: 0 12px 28px -14px rgba(11,31,58,0.45);
        }
        .login-portal .login-cta:hover {
          background: linear-gradient(95deg, #0b1f3a 0%, #d4af37 130%);
          box-shadow: 0 18px 36px -14px rgba(212,175,55,0.35), 0 8px 20px -10px rgba(11,31,58,0.4);
        }
        @media (prefers-reduced-motion: reduce) {
          .login-portal * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}
